import { useState, useEffect, useRef, useCallback } from 'react';
import { applyAITakeoverRatingAdjustment, calculateRatingChange } from '../utils/eloRating';
import { canCurrentPlayerSwap } from '../utils/botHelpers';
import { recordPlaceMove, recordSwapMove, finalizeGameLog } from '../utils/gameRecorder';
import { debug } from '../utils/logger';
import { supabase } from '../supabaseClient';
import { placeMarker, calculateScore, getNextActivePlayer } from '../utils/gameLogic';
import { resolveRoomSettings } from '../utils/roomSettings';
import {
  saveGame, updateStats, updatePlayerRatings, finalizeRoom,
  applyMove, findRejoinableGames,
  checkRoomDisconnects,
} from '../utils/supabaseActions';
import { mergeFinalPlayersWithRatings } from './useGameOrchestrator.helpers';
import { useGameOrchestratorRealtime } from './useGameOrchestrator.realtime';

const getHumanSeatNames = (roomData, finalPlayers) => {
  const roomSeatNames = Array.isArray(roomData?.players)
    ? roomData.players
      .filter((player) => player?.username && !player.isAI)
      .map((player) => player.username)
    : [];

  if (roomSeatNames.length > 0) {
    return new Set(roomSeatNames);
  }

  return new Set(
    finalPlayers
      .filter((player) => !player.isAI && !player.isBot)
      .map((player) => player.name)
  );
};

const getAITakeoverMoveCounts = (roomData, finalStateOverride) => {
  const moveCounts = finalStateOverride?.aiControlledMoveCounts ?? roomData?.game_state?.aiControlledMoveCounts ?? {};
  return moveCounts && typeof moveCounts === 'object' ? moveCounts : {};
};

const getSeatMoveCounts = (seatPlayers, finalStateOverride, fallbackMoves) => {
  const moves = Array.isArray(finalStateOverride?.moves)
    ? finalStateOverride.moves
    : (Array.isArray(fallbackMoves) ? fallbackMoves : []);

  if (!Array.isArray(seatPlayers) || seatPlayers.length === 0 || moves.length === 0) {
    return {};
  }

  return moves.reduce((counts, move) => {
    if (!Number.isInteger(move?.player)) return counts;
    const playerName = seatPlayers[move.player]?.name;
    if (!playerName) return counts;
    counts[playerName] = (counts[playerName] || 0) + 1;
    return counts;
  }, {});
};

/**
 * useGameOrchestrator — owns all in-game state, effects, and move handlers.
 *
 * Params:
 *   roomCode, roomData, isSpectator               — room identity (from App.js / useRoomLobby)
 *   setRoomCode, setRoomData, setIsSpectator       — setters (from App.js)
 *   setRejoinableGames                              — from App.js
 *   screen, setScreen                               — from App.js
 *   currentUser                                     — from App.js
 *   isBotFight                                      — true when in bot-fight spectator mode
 *   setLastExecutedAITurn, onMoveDone               — from useAIPlayer
 *   loadLeaderboard                                 — useCallback in App.js
 *   clearRoomChat                                   — from useChat
 *   addToast, t                                     — utilities
 */
export function useGameOrchestrator({
  roomCode,
  roomData,
  isSpectator,
  setRoomCode,
  setRoomData,
  setIsSpectator,
  setRejoinableGames,
  screen,
  setScreen,
  currentUser,
  isBotFight,
  setLastExecutedAITurn,
  onMoveDone,
  loadLeaderboard,
  refreshPresence,
  clearRoomChat,
  addToast,
  t,
}) {
  // ── Game state ────────────────────────────────────────────────────────────
  const [gameBoard, setGameBoard] = useState([]);
  const [players, setPlayers] = useState([]);
  const [currentPlayer, setCurrentPlayer] = useState(0);
  const [selectedMarker, setSelectedMarker] = useState(null);
  const [draggedMarker, setDraggedMarker] = useState(null);
  const [markersPool, setMarkersPool] = useState([]);
  const [moveHistory, setMoveHistory] = useState([]);
  const [gameEnded, setGameEnded] = useState(false);
  const [endGamePlayers, setEndGamePlayers] = useState(null);
  const [showGameEndModal, setShowGameEndModal] = useState(false);
  const [finalGamePlayers, setFinalGamePlayers] = useState([]);
  const [isConnectionOffline, setIsConnectionOffline] = useState(false);
  const [isConnectionRecovering, setIsConnectionRecovering] = useState(false);

  // ── Refs ──────────────────────────────────────────────────────────────────
  const gameEndingRef   = useRef(false);
  const gameLogRef      = useRef(null);
  const moveInProgressRef = useRef(false);
  const playersRef      = useRef([]);
  const gameStartTimeRef = useRef(null);
  const { swapLimitPerPlayer, turnTimerSeconds } = resolveRoomSettings(roomData);
  const turnDeadlineAt = roomData?.game_state?.turnDeadlineAt ?? null;

  // Mirror players into ref for compare-without-dependency (UX-5 reconnect toast)
  useEffect(() => {
    playersRef.current = players;
  }, [players]);

  useEffect(() => {
    if (typeof navigator === 'undefined') return;

    const handleOffline = () => {
      setIsConnectionOffline(true);
      setIsConnectionRecovering(false);
    };

    const handleOnline = () => {
      setIsConnectionOffline(false);
      setIsConnectionRecovering(true);
    };

    setIsConnectionOffline(!navigator.onLine);
    setIsConnectionRecovering(false);

    window.addEventListener('offline', handleOffline);
    window.addEventListener('online', handleOnline);

    return () => {
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('online', handleOnline);
    };
  }, []);

  // ── Callbacks ─────────────────────────────────────────────────────────────

  const handleGameEndModalClose = useCallback(async () => {
    const activeGames = await findRejoinableGames(currentUser);
    setRejoinableGames(activeGames);

    debug('[CLEAR] Clearing game state: roomCode, roomData');
    setRoomCode('');
    setRoomData(null);
    setIsSpectator(false);
    clearRoomChat();

    if (activeGames.length > 0) {
      setScreen('rejoin-or-new');
    } else {
      setScreen('create-or-join');
    }

    // Reset ref and close modal when returning to main menu
    gameEndingRef.current = false;
    gameLogRef.current = null;
    gameStartTimeRef.current = null;
    setShowGameEndModal(false);
  }, [currentUser, clearRoomChat, setRejoinableGames, setRoomCode, setRoomData, setIsSpectator, setScreen]);

  const syncRoomDataWithAuthoritativeGameState = useCallback((nextGameState) => {
    if (!nextGameState) return;

    const { gameOver, ...persistedGameState } = nextGameState;
    setRoomData((previousRoomData) => {
      if (!previousRoomData) return previousRoomData;

      return {
        ...previousRoomData,
        status: gameOver ? 'finished' : 'playing',
        game_state: persistedGameState,
      };
    });
  }, [setRoomData]);

  const endGame = useCallback(async (finalPlayers, finalStateOverride = null) => {
    debug('endGame called, gameEnded:', gameEnded);

    if (gameEndingRef.current || gameEnded) {
      debug('Game already ending/ended, skipping...');
      return;
    }

    gameEndingRef.current = true;
    setGameEnded(true);
    setLastExecutedAITurn(null);

    const sorted = [...finalPlayers].sort((a, b) => b.score - a.score);
    const topScore = sorted[0].score;
    const winners = sorted.filter(p => p.score === topScore).map(p => p.name);

    const humanSeatNames = getHumanSeatNames(roomData, finalPlayers);
    const aiTakeoverMoveCounts = getAITakeoverMoveCounts(roomData, finalStateOverride);
    const seatMoveCounts = getSeatMoveCounts(finalPlayers, finalStateOverride, moveHistory);
    const humanPlayers = finalPlayers.filter((player) => humanSeatNames.has(player.name));
    const humanNames = humanPlayers.map(p => p.name);

    const finalScores = {};
    finalPlayers.forEach(p => { finalScores[p.name] = p.score; });

    // --- ELO: Calculate rating changes (humans AND bots) ---
    const allScores = sorted.map(p => p.score);
    const ratingUpdates = [];
    const botRatingUpdates = [];

    sorted.forEach((player, idx) => {
      const myRating = player.rating || 1500;
      const opponentRatings = sorted
        .filter((_, i) => i !== idx)
        .map(p => p.rating || 1500);

      const aiControlledMoves = Number.isFinite(Number(aiTakeoverMoveCounts[player.name]))
        ? Number(aiTakeoverMoveCounts[player.name])
        : 0;
      const totalSeatMoves = Number.isFinite(Number(seatMoveCounts[player.name]))
        ? Number(seatMoveCounts[player.name])
        : aiControlledMoves;
      const rawChange = calculateRatingChange(
        myRating,
        player.score,
        allScores,
        opponentRatings,
        player.games_played || 0
      );
      const change = applyAITakeoverRatingAdjustment(rawChange, aiControlledMoves, totalSeatMoves);

      const update = {
        username: player.name,
        oldRating: myRating,
        newRating: myRating + change,
        change,
        rawChange,
        score: player.score || 0,
        aiControlledMoves,
        totalSeatMoves,
      };

      if (player.isBot) {
        botRatingUpdates.push(update);
      } else if (humanSeatNames.has(player.name)) {
        ratingUpdates.push(update);
      }
      // Human-owned seats still receive stats/ELO even if AI finished some turns.
      // Permanent handoffs become real bot seats in roomData.players and stay excluded.
    });

    const allRatingUpdates = [...ratingUpdates, ...botRatingUpdates];
    const sortedWithRatings = mergeFinalPlayersWithRatings(sorted, allRatingUpdates);

    const isHost = currentUser === roomData?.host;

    try {
      if (isHost) {
        if (humanNames.length > 0) {
          await updateStats(winners, humanNames, finalScores);
        }
        const savedGame = await saveGame({
          players: finalPlayers.map(p => p.name),
          finalScores,
          winners,
          ratingChanges: [...ratingUpdates, ...botRatingUpdates].length > 0
            ? [...ratingUpdates, ...botRatingUpdates]
            : null,
          gameLog: finalizeGameLog(gameLogRef.current),
        });

        if (ratingUpdates.length > 0) {
          await updatePlayerRatings(
            ratingUpdates.map(u => ({ ...u, gameId: savedGame?.id }))
          );
        }

        for (const botUpdate of botRatingUpdates) {
          const didWin = winners.includes(botUpdate.username);
          try {
            await supabase.rpc('update_bot_stats', {
              bot_username:    botUpdate.username,
              new_rating:      botUpdate.newRating,
              did_win:         didWin,
              bot_score:       botUpdate.score,
              p_game_id:       savedGame?.id || null,
              p_old_rating:    botUpdate.oldRating,
              p_rating_change: botUpdate.change,
            });
          } catch (err) {
            console.error(`Failed to update bot stats for ${botUpdate.username}:`, err);
          }
        }

        debug('Updating room status to finished...');
        await finalizeRoom(roomCode, sortedWithRatings, roomData?.game_state);
        try { localStorage.removeItem(`gamelog_${roomCode}`); } catch { /* ignore */ }
      } else {
        debug('Non-host client: skipping DB writes, host handles saves.');
        try { localStorage.removeItem(`gamelog_${roomCode}`); } catch { /* ignore */ }
      }

      await loadLeaderboard();
      // Refresh own presence so updated ELO is visible in the online list
      if (refreshPresence) refreshPresence().catch(() => {});
    } catch (e) {
      console.error('Failed to save game:', e);
    }

    debug('Showing game end modal for winners:', winners);
    setFinalGamePlayers(sortedWithRatings);
    setShowGameEndModal(true);
  }, [gameEnded, roomCode, currentUser, roomData, moveHistory, loadLeaderboard, refreshPresence, setLastExecutedAITurn]);

  // ── Bot Fight: single-write finalization ──────────────────────────────────
  // Used instead of endGame() when isBotFight=true.
  // ELO deltas are pre-computed client-side (same as endGame) and passed to
  // the finalize_bot_fight RPC which does one atomic DB write.
  const endBotFight = useCallback(async (finalPlayers, finalBoard, finalPool, finalMoves) => {
    if (gameEndingRef.current || gameEnded) return;
    gameEndingRef.current = true;
    setGameEnded(true);
    setLastExecutedAITurn(null);

    const sorted = [...finalPlayers].sort((a, b) => b.score - a.score);
    const allScores = sorted.map(p => p.score);

    const botRatingUpdates = sorted.map((player, idx) => {
      const myRating = player.rating || 1500;
      const opponentRatings = sorted.filter((_, i) => i !== idx).map(p => p.rating || 1500);
      const change = calculateRatingChange(myRating, player.score, allScores, opponentRatings, player.games_played || 0);
      return { username: player.name, oldRating: myRating, newRating: myRating + change, change, score: player.score || 0 };
    });

    const playersWithRatings = finalPlayers.map(p => {
      const u = botRatingUpdates.find(u => u.username === p.name);
      return u ? { ...p, ratingChange: u.change, newRating: u.newRating } : p;
    });

    // Only send fields the DB function needs — strips Supabase profile `id` (UUID)
    // and other non-game fields that could cause type-cast errors in the RPC.
    const cleanPlayers = playersWithRatings.map(p => ({
      name: p.name,
      score: p.score || 0,
      colorIndex: p.colorIndex,
      markers: p.markers,
      rating: p.rating || 1500,
      ratingChange: p.ratingChange || 0,
      newRating: p.newRating || (p.rating || 1500),
      games_played: p.games_played || 0,
    }));

    const finalStateForDB = {
      gameBoard: finalBoard ?? gameBoard,
      players: cleanPlayers,
      currentPlayer,
      markersPool: finalPool ?? markersPool,
      moves: finalMoves ?? moveHistory,
      game_log: finalizeGameLog(gameLogRef.current),
    };

    const sortedWithRatings = sorted.map(p => {
      const u = botRatingUpdates.find(u => u.username === p.name);
      return u ? { ...p, ratingChange: u.change, newRating: u.newRating } : p;
    });

    const { error: finalizeError } = await supabase.rpc('finalize_bot_fight', { p_code: roomCode, p_final_state: finalStateForDB });
    if (finalizeError) {
      console.error('[Bot Fight] finalize_bot_fight failed:', finalizeError.message, finalizeError);
    } else {
      await loadLeaderboard();
    }

    setFinalGamePlayers(sortedWithRatings);
    setShowGameEndModal(true);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameEnded, roomCode, gameBoard, currentPlayer, markersPool, moveHistory, loadLeaderboard, setLastExecutedAITurn]);

  // ── Effects ───────────────────────────────────────────────────────────────

  // Reset gameEndingRef on screen transitions
  useEffect(() => {
    if (screen === 'create-or-join' || screen === 'rejoin-or-new') {
      gameEndingRef.current = false;
    } else if (screen === 'game' && !gameEnded) {
      gameEndingRef.current = false;
    }
  }, [screen, gameEnded]);

  // Restore game log from localStorage when reconnecting after a reload
  useEffect(() => {
    if (screen === 'game' && !gameLogRef.current && roomCode) {
      try {
        const saved = localStorage.getItem(`gamelog_${roomCode}`);
        if (saved) {
          gameLogRef.current = JSON.parse(saved);
          debug('Game log restored from localStorage after reload');
        }
      } catch (e) {
        console.error('Failed to restore game log from localStorage:', e);
      }
    }
  }, [screen, roomCode]);

  useGameOrchestratorRealtime({
    isBotFight,
    roomCode,
    screen,
    gameEnded,
    isSpectator,
    setIsConnectionOffline,
    setIsConnectionRecovering,
    setRoomData,
    setPlayers,
    setGameBoard,
    setGameEnded,
    setFinalGamePlayers,
    setShowGameEndModal,
    setEndGamePlayers,
    setCurrentPlayer,
    setMarkersPool,
    setMoveHistory,
    setScreen,
    setLastExecutedAITurn,
    playersRef,
    addToast,
    t,
    gameLogRef,
    gameStartTimeRef,
    gameEndingRef,
  });

  // Handle game end when endGamePlayers is set
  useEffect(() => {
    if (endGamePlayers && !gameEnded && !isSpectator) {
      debug('endGamePlayers effect triggered, calling endGame...');
      endGame(endGamePlayers);
      setEndGamePlayers(null);
    }
  }, [endGamePlayers, gameEnded, endGame, isSpectator]);

  // Trigger the shared server-side disconnect sweep every 10 seconds while connected.
  useEffect(() => {
    if (!roomCode || screen !== 'game' || isSpectator || isBotFight) return;

    const interval = setInterval(async () => {
      if (typeof navigator !== 'undefined' && !navigator.onLine) return;
      try {
        await checkRoomDisconnects(roomCode);
      } catch (e) {
        console.error('Error in disconnect check:', e);
      }
    }, 10000);

    return () => clearInterval(interval);
  }, [roomCode, screen, isSpectator, isBotFight]);

  // Trigger the shared room sweep when the visible turn deadline expires.
  useEffect(() => {
    if (!roomCode || screen !== 'game' || isSpectator || isBotFight) return;
    if (typeof turnTimerSeconds !== 'number' || turnTimerSeconds <= 0 || !turnDeadlineAt) return;

    const activePlayer = players[currentPlayer];
    if (!activePlayer || activePlayer.isAI) return;

    const deadlineMs = Date.parse(turnDeadlineAt);
    if (!Number.isFinite(deadlineMs)) return;

    const triggerSweep = async () => {
      if (typeof navigator !== 'undefined' && !navigator.onLine) return;
      try {
        await checkRoomDisconnects(roomCode);
      } catch (e) {
        console.error('Error in timed turn check:', e);
      }
    };

    if (deadlineMs <= Date.now()) {
      triggerSweep();
      return undefined;
    }

    const timeoutId = setTimeout(triggerSweep, Math.max(0, deadlineMs - Date.now()) + 150);
    return () => clearTimeout(timeoutId);
  }, [roomCode, screen, isSpectator, isBotFight, turnTimerSeconds, turnDeadlineAt, players, currentPlayer]);

  // ── Move handlers ─────────────────────────────────────────────────────────

  // Local move execution for bot-fight mode — no RPC, no Realtime
  const executeMoveLocally = useCallback((markerIndex, row, col) => {
    const player = players[currentPlayer];
    // Use the actual marker's colorIndex — players can hold markers of ANY color,
    // not just their own. The placed color affects the score of whichever player
    // owns that colorIndex, not necessarily the player who placed it.
    const marker = player.markers[markerIndex];
    const placedColorIndex = marker.colorIndex;
    const newBoard = placeMarker(gameBoard, row, col, placedColorIndex);

    const newMarkers = [...player.markers];
    newMarkers.splice(markerIndex, 1);
    let newPool = [...markersPool];
    if (newPool.length > 0) {
      newMarkers.push(newPool[0]);
      newPool = newPool.slice(1);
    }

    const newPlayers = players.map((p, idx) => ({
      ...p,
      // Recalculate score only for the player whose color was placed
      score:   p.colorIndex === placedColorIndex ? calculateScore(newBoard, p.colorIndex) : p.score,
      markers: idx === currentPlayer ? newMarkers : p.markers,
    }));

    const nextPlayer = getNextActivePlayer(currentPlayer, newPlayers);
    const gameOver   = nextPlayer === null;

    const moveRecord = { type: 'place', player: currentPlayer, row, col, playerName: player.name, colorIndex: placedColorIndex };
    recordPlaceMove(gameLogRef.current, currentPlayer, placedColorIndex, row, col);

    setGameBoard(newBoard);
    setPlayers(newPlayers);
    setMarkersPool(newPool);
    setMoveHistory(prev => [...prev, moveRecord]);
    setCurrentPlayer(gameOver ? currentPlayer : (nextPlayer ?? currentPlayer));

    if (gameOver) {
      setTimeout(() => endBotFight(newPlayers, newBoard, newPool, [...moveHistory, moveRecord]), 600);
    }
  }, [players, currentPlayer, gameBoard, markersPool, moveHistory, endBotFight]);

  const swapMarkersLocally = useCallback((targetPlayerId) => {
    if (!canCurrentPlayerSwap({ players, markersPool, moveHistory, currentPlayer, swapLimitPerPlayer })) return;
    const newPlayers = players.map((p, idx) => {
      if (idx === currentPlayer) return { ...p, markers: [...players[targetPlayerId].markers] };
      if (idx === targetPlayerId) return { ...p, markers: [...players[currentPlayer].markers] };
      return p;
    });

    const nextPlayer = getNextActivePlayer(currentPlayer, newPlayers);
    const gameOver   = nextPlayer === null;
    const moveRecord = { type: 'swap', player: currentPlayer, target: targetPlayerId, playerName: players[currentPlayer].name, targetName: players[targetPlayerId].name };
    recordSwapMove(gameLogRef.current, currentPlayer, targetPlayerId);

    setPlayers(newPlayers);
    setMoveHistory(prev => [...prev, moveRecord]);
    setCurrentPlayer(gameOver ? currentPlayer : (nextPlayer ?? currentPlayer));

    if (gameOver) {
      setTimeout(() => endBotFight(newPlayers, null, null, [...moveHistory, moveRecord]), 600);
    }
  }, [players, currentPlayer, markersPool, moveHistory, endBotFight, swapLimitPerPlayer]);

  const handleExpiredTurn = useCallback(async () => {
    addToast(t('timerExpired'), 'warning', 3000);
    try {
      await checkRoomDisconnects(roomCode);
    } catch (e) {
      console.error('Error while syncing expired turn:', e);
    }
  }, [addToast, roomCode, t]);

  const buildExpectedTurnState = useCallback((override = null) => {
    const turnState = override && typeof override === 'object'
      ? override
      : {
          expectedCurrentPlayerIndex: currentPlayer,
          expectedCurrentPlayerName: players[currentPlayer]?.name,
          expectedCurrentPlayerIsAI: Boolean(players[currentPlayer]?.isAI),
        };

    const expectedTurnState = {};

    if (Number.isInteger(turnState.expectedCurrentPlayerIndex)) {
      expectedTurnState.expectedCurrentPlayerIndex = turnState.expectedCurrentPlayerIndex;
    }

    if (typeof turnState.expectedCurrentPlayerName === 'string' && turnState.expectedCurrentPlayerName.length > 0) {
      expectedTurnState.expectedCurrentPlayerName = turnState.expectedCurrentPlayerName;
    }

    if (typeof turnState.expectedCurrentPlayerIsAI === 'boolean') {
      expectedTurnState.expectedCurrentPlayerIsAI = turnState.expectedCurrentPlayerIsAI;
    }

    return expectedTurnState;
  }, [currentPlayer, players]);

  const executeMove = async (markerIndex, row, col, turnStateOverride = null) => {
    if (isBotFight) {
      executeMoveLocally(markerIndex, row, col);
      return;
    }
    // Capture placed color BEFORE await — players state is stable in this render closure
    const placedColorIndex = players[currentPlayer]?.markers?.[markerIndex]?.colorIndex;
    // Snapshot log length before RPC — used to detect if the Realtime subscription
    // already recorded this move while we were awaiting the response.
    const logLenBefore = gameLogRef.current?.moves?.length ?? 0;
    let result;
    try {
      const expectedTurnState = buildExpectedTurnState(turnStateOverride);
      // SEC-AUTH-1: server validates turn, cell, marker; computes new state atomically
      result = await applyMove(roomCode, {
        type: 'place',
        markerIndex,
        row,
        col,
        ...expectedTurnState,
      });
    } catch (error) {
      if (error?.message === 'Turn timer expired') {
        await handleExpiredTurn();
        return;
      }
      throw error;
    }

    // Record AFTER server confirms the move — prevents phantom entries when RPC fails.
    // Guard: the Realtime subscription handler may have already recorded this move
    // (race between RPC response and Realtime callback). Only record if the log
    // hasn't grown while we were awaiting.
    if (placedColorIndex !== undefined && gameLogRef.current && gameLogRef.current.moves.length === logLenBefore) {
      recordPlaceMove(gameLogRef.current, currentPlayer, placedColorIndex, row, col);
      try { localStorage.setItem(`gamelog_${roomCode}`, JSON.stringify(gameLogRef.current)); } catch { /* ignore */ }
    }

    // Apply server-authoritative state
    setGameBoard(result.gameBoard);
    setPlayers(result.players);
    setMarkersPool(result.markersPool);
    setMoveHistory(result.moves);
    setCurrentPlayer(result.currentPlayer);
    syncRoomDataWithAuthoritativeGameState(result);

    if (result.gameOver) {
      debug('Game end detected via apply_move, calling endGame...');
      // No setTimeout: start DB writes immediately so iOS cannot suspend the page
      // before stats/ratings are persisted (endGame shows modal only after all awaits).
      endGame(result.players, result);
    }
  };

  const handleCellClick = async (row, col, markerOverride, turnStateOverride = null) => {
    if (isSpectator) return;
    if (isConnectionOffline || isConnectionRecovering) {
      addToast(t('actionsBlockedOffline'), 'warning', 3000);
      return;
    }
    if (!isBotFight && moveInProgressRef.current) return;

    const currentPlayerData = players[currentPlayer];
    if (!currentPlayerData?.isAI && currentPlayerData?.name !== currentUser) return;

    const markerIndex = markerOverride !== undefined ? markerOverride : selectedMarker;

    if (markerIndex !== null && gameBoard[row][col] === 'Y') {
      if (!isBotFight) moveInProgressRef.current = true;
      try {
        setSelectedMarker(null);
        await executeMove(markerIndex, row, col, turnStateOverride);
      } finally {
        if (!isBotFight) moveInProgressRef.current = false;
        onMoveDone();
      }
    }
  };

  const handleDragStart = (markerIdx) => {
    setDraggedMarker(markerIdx);
  };

  const handleDragEnd = () => {
    setDraggedMarker(null);
  };

  const handleDrop = async (row, col) => {
    if (isSpectator) return;
    if (!isBotFight && moveInProgressRef.current) return;
    if (draggedMarker !== null && gameBoard[row][col] === 'Y') {
      if (!isBotFight) moveInProgressRef.current = true;
      try {
        const markerIndex = draggedMarker;
        setDraggedMarker(null);
        await executeMove(markerIndex, row, col);
      } finally {
        if (!isBotFight) moveInProgressRef.current = false;
        onMoveDone();
      }
    }
  };

  const handleDragOver = (e, canDrop) => {
    if (canDrop) {
      e.preventDefault();
    }
  };

  const swapMarkers = async (targetPlayerId, turnStateOverride = null) => {
    if (isSpectator) return;
    if (isConnectionOffline || isConnectionRecovering) {
      addToast(t('actionsBlockedOffline'), 'warning', 3000);
      return;
    }
    if (!canCurrentPlayerSwap({ players, markersPool, moveHistory, currentPlayer, swapLimitPerPlayer })) return;
    if (moveInProgressRef.current) return;

    if (isBotFight) {
      swapMarkersLocally(targetPlayerId);
      onMoveDone();
      return;
    }

    moveInProgressRef.current = true;
    // Snapshot log length before RPC — used to detect if the Realtime subscription
    // already recorded this move while we were awaiting the response.
    const logLenBeforeSwap = gameLogRef.current?.moves?.length ?? 0;
    try {
      let result;
      try {
        const expectedTurnState = buildExpectedTurnState(turnStateOverride);
        // SEC-AUTH-1: server validates turn, target, pool; swaps atomically
        result = await applyMove(roomCode, {
          type: 'swap',
          targetPlayerIndex: targetPlayerId,
          ...expectedTurnState,
        });
      } catch (error) {
        if (error?.message === 'Turn timer expired') {
          await handleExpiredTurn();
          return;
        }
        throw error;
      }

      // Record AFTER server confirms the swap — prevents phantom entries on RPC failure.
      // Guard: only record if the Realtime subscription hasn't already done it.
      if (gameLogRef.current && gameLogRef.current.moves.length === logLenBeforeSwap) {
        recordSwapMove(gameLogRef.current, currentPlayer, targetPlayerId);
        try { localStorage.setItem(`gamelog_${roomCode}`, JSON.stringify(gameLogRef.current)); } catch { /* ignore */ }
      }

      // Apply server-authoritative state
      setGameBoard(result.gameBoard);
      setPlayers(result.players);
      setMarkersPool(result.markersPool);
      setMoveHistory(result.moves);
      setSelectedMarker(null);
      setCurrentPlayer(result.currentPlayer);
      syncRoomDataWithAuthoritativeGameState(result);

      if (result.gameOver) {
        endGame(result.players, result);
      }
    } finally {
      moveInProgressRef.current = false;
      onMoveDone();
    }
  };

  // ── Heartbeat for disconnect detection (NET-2) ────────────────────────────
  useEffect(() => {
    if (!roomCode || screen !== 'game' || isSpectator || isBotFight) return;
    const send = () => {
      supabase.rpc('update_player_heartbeat', { p_room_code: roomCode, p_username: currentUser }).then(() => {});
    };
    send(); // immediate first heartbeat
    const id = setInterval(send, 30000); // every 30s
    return () => clearInterval(id);
  }, [roomCode, screen, isSpectator, isBotFight, currentUser]);

  // ── Return ─────────────────────────────────────────────────────────────────
  return {
    // State
    gameBoard, setGameBoard,
    players, setPlayers,
    currentPlayer, setCurrentPlayer,
    selectedMarker, setSelectedMarker,
    draggedMarker, setDraggedMarker,
    markersPool, setMarkersPool,
    moveHistory, setMoveHistory,
    gameEnded, setGameEnded,
    endGamePlayers, setEndGamePlayers,
    showGameEndModal, setShowGameEndModal,
    finalGamePlayers, setFinalGamePlayers,
    isConnectionOffline,
    isConnectionRecovering,
    // Refs (exposed for useRoomLobby + gameActionsRef population)
    gameEndingRef,
    gameLogRef,
    moveInProgressRef,
    gameStartTimeRef,
    // Callbacks
    endGame,
    endBotFight,
    handleGameEndModalClose,
    executeMove,
    handleCellClick,
    handleDrop,
    handleDragStart,
    handleDragEnd,
    handleDragOver,
    swapMarkers,
  };
}
